import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Aba controlada pela **URL** em vez de `useState`.
 *
 * A aba é um segmento opcional da rota (`:tab?`), então o endereço é
 * compartilhável e sobrevive a refresh/voltar — diferente de guardar a aba em
 * estado local, que se perde a cada carregamento.
 *
 * Como o segmento é opcional, trocar de aba só muda o param (a rota casada é a
 * mesma): o componente **não remonta**, preservando o estado interno da página.
 *
 * @param basePath  caminho da página, sem a aba (ex.: `/sales/123`)
 * @param tabIds    ids válidos, na ordem em que aparecem
 * @param defaultTab aba usada quando a URL não traz nenhuma (padrão: a primeira)
 */
export function useRouteTabs(
  basePath: string,
  tabIds: string[],
  defaultTab?: string,
) {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();

  const fallback = defaultTab ?? tabIds[0];
  // Aba desconhecida na URL cai no padrão (link velho/digitado errado).
  const activeTab = tab && tabIds.includes(tab) ? tab : fallback;

  const onTabChange = useCallback(
    (tabId: string) => {
      void navigate(`${basePath}/${tabId}`);
    },
    [basePath, navigate],
  );

  return { activeTab, onTabChange };
}
